import Highcharts from 'highcharts';
import HighchartsReact from "highcharts-react-official";
import React, { useState, useMemo, useRef, useContext } from 'react';
import moment from 'moment';
import { useTheme } from '../ThemeContext';
import { Lock } from 'lucide-react';
import { UserContext } from '../../context/UserContext';

interface MChartProps {
    periods: string[];
    sliderVisible?: boolean;
    rangeVisible?: boolean;
    seriesData: {
        yAxis: Highcharts.YAxisOptions;
        series: Highcharts.SeriesOptionsType;
    }[];
    onChange?: (period: string) => void;
    selectedPeriod?: string;
    height?: number;
}
const MChart: React.FC<MChartProps> = ({ periods, seriesData, onChange, sliderVisible = true, rangeVisible = true, selectedPeriod, height }) => {
    // console.log("Multichart Props:", periods, seriesData);
    // const [selectedPeriod, setSelectedPeriod] = useState<string>('annual');
    const [selectedRange, setSelectedRange] = useState<string>('5y');
    const [yearRange, setYearRange] = useState<[number, number]>([0, 100]);
    const sliderRef = useRef<HTMLDivElement>(null);
    const { theme } = useTheme();
    const { userData } = useContext(UserContext) || {};
    const isLoggedIn = typeof window !== 'undefined' && Boolean(sessionStorage.getItem('STOCK_AUTH_TOKEN'));
    const hasAccess = isLoggedIn && (userData?.planActive === true || userData?.subscriptionStatus === 'active' || userData?.subscriptionStatus === 'trialing');

    const isQuarterlyData = useMemo(() => {
        return periods.some(period => period.toString().includes('Q'));
    }, [periods]);

    React.useEffect(() => {
        if (!hasAccess && (selectedRange === '7y' || selectedRange === 'All')) {
            setSelectedRange('5y');
        }
    }, [hasAccess, selectedRange]);

    const availableYearRange = useMemo(() => {
        if (!periods || periods.length === 0) return { min: 2020, max: 2030 };

        const years = periods.map(period => {
            const yearMatch = period.toString().match(/(\d{4})/);
            const year = yearMatch ? parseInt(yearMatch[1]) : 2025;
            return year;
        });

        return {
            min: Math.min(...years),
            max: Math.max(...years)
        };
    }, [periods]);

    // Create period markers for quarterly data
    const periodMarkers = useMemo(() => {
        if (!isQuarterlyData) {
            const totalYears = availableYearRange.max - availableYearRange.min;
            const markers = [];

            for (let i = 0; i <= totalYears; i++) {
                const year = availableYearRange.min + i;
                const position = totalYears > 0 ? (i / totalYears) * 100 : 50;
                markers.push({ year, position });
            }
            return markers;
        } else {
            return periods.map((period, index) => ({
                period: period.toString(),
                position: periods.length > 1 ? (index / (periods.length - 1)) * 100 : 50,
                index
            }));
        }
    }, [periods, availableYearRange, isQuarterlyData]);

    const filteredData = useMemo(() => {
        if (!periods || periods.length === 0) return { periods, seriesData };

        let startIndex = 0;
        let endIndex = periods.length - 1;

        if (selectedRange !== 'All') {
            if (isQuarterlyData) {
                const currentYear = 2025;
                let yearsBack = 0;

                switch (selectedRange) {
                    case '1y':
                        yearsBack = 1;
                        break;
                    case '5y':
                        yearsBack = 5;
                        break;
                    case '7y':
                        yearsBack = 7;
                        break;
                }

                if (yearsBack > 0) {
                    const cutoffYear = currentYear - yearsBack;
                    const rangeIndices = periods.map((period, index) => {
                        const yearMatch = period.toString().match(/(\d{4})/);
                        const periodYear = yearMatch ? parseInt(yearMatch[1]) : 2025;
                        return periodYear >= cutoffYear ? index : -1;
                    }).filter(index => index !== -1);

                    if (rangeIndices.length > 0) {
                        startIndex = Math.min(...rangeIndices);
                        endIndex = Math.max(...rangeIndices);
                    }
                }
            } else {
                // Annual data filtering (existing logic)
                let startIndex = 0;
                let endIndex = periods.length - 1;

                if (selectedRange !== 'All') {
                    const currentYear = 2025; // Use 2025 as TTM base year
                    let yearsBack = 0;

                    switch (selectedRange) {
                        case '1y':
                            yearsBack = 1;
                            break;
                        case '5y':
                            yearsBack = 5;
                            break;
                        case '7y':
                            yearsBack = 7;
                            break;
                    }

                    if (yearsBack > 0) {
                        const cutoffYear = currentYear - yearsBack;
                        const rangeIndices = periods.map((period, index) => {
                            const year = moment(period, ['YYYY', 'MMM YYYY', 'DD MMM YYYY']).year();
                            const periodYear = isNaN(year) ? 2025 : year;
                            return periodYear >= cutoffYear ? index : -1;
                        }).filter(index => index !== -1);

                        if (rangeIndices.length > 0) {
                            startIndex = Math.min(...rangeIndices);
                            endIndex = Math.max(...rangeIndices);
                        }
                    }
                }

                if (selectedRange === 'All') {
                    startIndex = Math.floor((yearRange[0] / 100) * (periods.length - 1));
                    endIndex = Math.ceil((yearRange[1] / 100) * (periods.length - 1));
                }
                console.log("Filtered Periods:", periods);
                const filteredPeriods = periods.slice(startIndex, endIndex + 1);
                const filteredSeriesData = seriesData.map(item => ({
                    ...item,
                    series: {
                        ...item.series,
                        data: Array.isArray(item.series.data)
                            ? item.series.data.slice(startIndex, endIndex + 1)
                            : item.series.data
                    }
                }));

                return {
                    periods: filteredPeriods,
                    seriesData: filteredSeriesData
                };
            }
        }

        if (selectedRange === 'All') {
            startIndex = Math.floor((yearRange[0] / 100) * (periods.length - 1));
            endIndex = Math.ceil((yearRange[1] / 100) * (periods.length - 1));
        }

        const filteredPeriods = (periods[0] == "TTM") ? periods.slice(startIndex, endIndex + 1) : periods.slice(startIndex, endIndex + 1);
        const filteredSeriesData = seriesData.map(item => ({
            ...item,
            series: {
                ...item.series,
                data: Array.isArray(item.series.data)
                    ? item.series.data.slice(startIndex, endIndex + 1)
                    : item.series.data
            }
        }));

        return {
            periods: filteredPeriods,
            seriesData: filteredSeriesData
        };
    }, [periods, seriesData, selectedRange, yearRange, isQuarterlyData]);

    const legendEnabled = filteredData.seriesData.length > 1;

    const options: Highcharts.Options = {
        chart: {
            showAxes: true,
            backgroundColor: 'transparent',
            style: {
                fontFamily: 'Arial, sans-serif',
                color: "var(--text-primary)",
            },
            credits: { enabled: true, text: 'Powered by StockFortress', href: 'https://stockfortress.com', style: { color: 'rgba(255, 255, 255, 0.4)', fontSize: '10px' } },
            zoomType: 'x',
            height: height ?? 400,
            // spacingTop: 16,
            // spacingBottom: 120,
            // marginTop: 8,
            // marginBottom: 72,
            events: {
                load: function () {
                    const chart = this as Highcharts.Chart;
                    // Defer to next frame to ensure container exists and layout is stable
                    if (typeof window !== 'undefined') {
                        requestAnimationFrame(() => {
                            try {
                                const hasContainer = !!(chart && (chart as any).renderTo);
                                if (hasContainer && typeof chart.reflow === 'function') {
                                    chart.reflow();
                                }
                            } catch (e) {
                                // Swallow reflow errors that can occur if unmounted quickly
                                // console.debug('Highcharts reflow skipped:', e);
                            }
                        });
                    }
                }
            }

        },
        credits: {
            enabled: true,
            text: 'Powered by StockFortress',
            href: 'https://stockfortress.com',
            style: {
                color: 'rgba(255, 255, 255, 0.4)',
                fontSize: '10px'
            }
        },
        title: {
            text: '',
        },
        subtitle: {
            text: '',
        },
        xAxis: [{
            categories: filteredData.periods,
            crosshair: true,
            labels: {
                rotation: 0,
                align: 'center',
                y: 20,
                style: {
                    color: "var(--text-primary)",
                    fontSize: '12px',
                },
            },
        }],
        yAxis: filteredData.seriesData.map((item) => item.yAxis),
        tooltip: {
            shared: false,
        },

        rangeSelector: {
            enabled: false
        },
        legend: {
            enabled: true,
            layout: 'horizontal',
            verticalAlign: 'bottom',
            align: 'center',
            floating: false,
            y: 0,
            // padding: 8,
            margin: 20,
            // itemMarginTop: 4,
            // itemMarginBottom: 4,
            maxHeight: 72,
            navigation: {
                enabled: true,
                activecolor: "var(--text-primary)",
                animation: true
            },
            backgroundColor: 'rgba(255,255,255,0.04)',
            borderWidth: 1,
            borderColor: '#4B5563',
            itemStyle: {
                color: "var(--text-primary)",
                fontSize: '12px',
                fontWeight: 'normal',
                textOutline: 'none',
                backgroundColor: 'rgba(0, 0, 0, 0)',
            },
            itemHoverStyle: {
                color: Highcharts.getOptions().colors?.[0].toString() || '#7cb5ec',
            },
        },
        series: filteredData.seriesData.map((item) => item.series as Highcharts.SeriesOptionsType),
    };

    const periodOptions = [
        { value: 'annual', label: 'Yearly' },
        { value: 'quarter', label: 'Quarterly' }
    ];

    const rangeOptions = [
        { value: '1y', label: '1 Year (TTM)' },
        { value: '5y', label: '5 Years' },
        { value: '7y', label: '7 Years' },
        { value: 'All', label: 'All' }
    ];

    const handlePeriodChange = (period: string) => {
        // setSelectedPeriod(period);
        if (onChange) {
            onChange(period);
        }
    };

    const handleRangeChange = (range: string) => {
        if (!hasAccess && (range === '7y' || range === 'All')) return;
        setSelectedRange(range);
        if (range !== 'All') {
            setYearRange([0, 100]); // Reset year slider when using preset ranges
        }
    };

    const handleYearRangeChange = (index: number, value: number) => {
        const newRange: [number, number] = [...yearRange];
        newRange[index] = value;

        // Ensure start is not greater than end
        if (index === 0 && newRange[0] > newRange[1]) {
            newRange[1] = newRange[0];
        }
        if (index === 1 && newRange[1] < newRange[0]) {
            newRange[0] = newRange[1];
        }

        setYearRange(newRange);
        setSelectedRange('All');
    };

    const resetYearRange = () => {
        setYearRange([0, 100]);
        setSelectedRange('All');
    };

    const handleSliderClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickPercent = (clickX / rect.width) * 100;

        const distanceToStart = Math.abs(clickPercent - yearRange[0]);
        const distanceToEnd = Math.abs(clickPercent - yearRange[1]);

        if (distanceToStart < distanceToEnd) {
            handleYearRangeChange(0, Math.max(0, Math.min(100, clickPercent)));
        } else {
            handleYearRangeChange(1, Math.max(0, Math.min(100, clickPercent)));
        }
    };

    return (
        <div style={{ width: '100%' }}>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0px',
                marginBottom: '0px',
                padding: '15px',
                backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
                borderRadius: '8px'
            }}>
                {rangeVisible && (
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        {/* Period Buttons */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <label style={{
                                color: '#9CA3AF',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}>
                                Period:
                            </label>
                            {periodOptions.map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => handlePeriodChange(option.value)}
                                    className={`btn ${selectedPeriod === option.value ? 'active' : ''}`}
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                        {/* Range Buttons */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <label style={{
                                color: '#9CA3AF',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}>
                                Range:
                            </label>
                            {rangeOptions.map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => handleRangeChange(option.value)}
                                    className={`btn ${selectedRange === option.value ? 'active' : ''}`}
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        cursor: (!hasAccess && (option.value === '7y' || option.value === 'All')) ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    disabled={!hasAccess && (option.value === '7y' || option.value === 'All')}
                                >
                                    {option.label}
                                    {!hasAccess && (option.value === '7y' || option.value === 'All') && selectedRange !== option.value && (
                                        <Lock size={12} className="ml-1 inline-block align-middle" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>)}
                <HighchartsReact
                    highcharts={Highcharts}
                    options={options}
                    containerProps={{ style: { height: height ? `${height}px` : undefined, width: '100%' } }}
                />
                {selectedRange === 'All' && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px'
                    }}>
                        {/* <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <span style={{
                                color: '#9CA3AF',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}>
                                {isQuarterlyData 
                                    ? `Range: ${Math.floor(periods.length * (yearRange[0] / 100)) + 1} to ${Math.ceil(periods.length * (yearRange[1] / 100))} of ${periods.length} periods`
                                    : `Custom Range: ${availableYearRange.min + Math.floor((yearRange[0] / 100) * (availableYearRange.max - availableYearRange.min))} - ${availableYearRange.min + Math.floor((yearRange[1] / 100) * (availableYearRange.max - availableYearRange.min))}`
                                }
                            </span>
                            <button
                                onClick={resetYearRange}
                                style={{
                                    padding: '4px 12px',
                                    backgroundColor: '#4F46E5',
                                    color: "var(--text-primary)",
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    cursor: 'pointer'
                                }}
                            >
                                Reset
                            </button>
                        </div> */}
                        {sliderVisible && (<>
                            {/* <div
                                ref={sliderRef}
                                style={{ position: 'relative', height: '50px', cursor: 'pointer' }}
                                onClick={handleSliderClick}
                            >
                            <div style={{
                                position: 'absolute',
                                top: '23px',
                                left: '0',
                                right: '0',
                                height: '4px',
                                backgroundColor: '#6B7280',
                                borderRadius: '2px'
                            }} />

                            <div style={{
                                position: 'absolute',
                                top: '23px',
                                left: `${yearRange[0]}%`,
                                width: `${yearRange[1] - yearRange[0]}%`,
                                height: '4px',
                                backgroundColor: '#4F46E5',
                                borderRadius: '2px'
                            }} />

                            {isQuarterlyData ? (
                                // Quarterly period markers
                                periodMarkers.map((marker, index) => {
                                    const shouldShow = periods.length <= 20 || index % Math.ceil(periods.length / 10) === 0;
                                    if (!shouldShow) return null;
                                    
                                    return (
                                        <div
                                            key={index}
                                            style={{
                                                position: 'absolute',
                                                top: '20px',
                                                left: `calc(${marker.position}% - 1px)`,
                                                width: '2px',
                                                height: '10px',
                                                backgroundColor: '#9CA3AF',
                                                borderRadius: '1px',
                                                pointerEvents: 'none',
                                                zIndex: 1
                                            }}
                                            title={marker.period}
                                        />
                                    );
                                })
                            ) : (
                                periodMarkers.map((marker) => (
                                    <div
                                        key={marker.year}
                                        style={{
                                            position: 'absolute',
                                            top: '20px',
                                            left: `calc(${marker.position}% - 2px)`,
                                            width: '4px',
                                            height: '10px',
                                            backgroundColor: '#9CA3AF',
                                            borderRadius: '2px',
                                            pointerEvents: 'none',
                                            zIndex: 1
                                        }}
                                        title={`${marker.year}`}
                                    />
                                ))
                            )}

                            <input
                                type="range"
                                min="0"
                                max="100"
                                step="1"
                                value={yearRange[0]}
                                onChange={(e) => handleYearRangeChange(0, parseInt(e.target.value))}
                                style={{
                                    position: 'absolute',
                                    opacity: 0,
                                    pointerEvents: 'none',
                                    width: '1px',
                                    height: '1px'
                                }}
                                tabIndex={-1}
                            />

                            <input
                                type="range"
                                min="0"
                                max="100"
                                step="1"
                                value={yearRange[1]}
                                onChange={(e) => handleYearRangeChange(1, parseInt(e.target.value))}
                                style={{
                                    position: 'absolute',
                                    opacity: 0,
                                    pointerEvents: 'none',
                                    width: '1px',
                                    height: '1px'
                                }}
                                tabIndex={-1}
                            />

                            <div
                                style={{
                                    position: 'absolute',
                                    top: '15px',
                                    left: `calc(${yearRange[0]}% - 10px)`,
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: '#4F46E5',
                                    borderRadius: '50%',
                                    border: '2px solid white',
                                    cursor: 'grab',
                                    zIndex: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();

                                    const startMouseX = e.clientX;
                                    const startValue = yearRange[0];

                                    const handleMouseMove = (moveEvent: MouseEvent) => {
                                        if (!sliderRef.current) return;

                                        const rect = sliderRef.current.getBoundingClientRect();
                                        const deltaX = moveEvent.clientX - startMouseX;
                                        const deltaPercent = (deltaX / rect.width) * 100;
                                        const newValue = Math.max(0, Math.min(yearRange[1], startValue + deltaPercent));

                                        handleYearRangeChange(0, newValue);
                                    };

                                    const handleMouseUp = () => {
                                        document.removeEventListener('mousemove', handleMouseMove);
                                        document.removeEventListener('mouseup', handleMouseUp);
                                    };

                                    document.addEventListener('mousemove', handleMouseMove);
                                    document.addEventListener('mouseup', handleMouseUp);
                                }}
                            >
                                <div style={{
                                    width: '6px',
                                    height: '6px',
                                    backgroundcolor: "var(--text-primary)",
                                    borderRadius: '50%'
                                }} />
                            </div>

                            <div
                                style={{
                                    position: 'absolute',
                                    top: '15px',
                                    left: `calc(${yearRange[1]}% - 10px)`,
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: '#4F46E5',
                                    borderRadius: '50%',
                                    border: '2px solid white',
                                    cursor: 'grab',
                                    zIndex: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();

                                    const startMouseX = e.clientX;
                                    const startValue = yearRange[1];

                                    const handleMouseMove = (moveEvent: MouseEvent) => {
                                        if (!sliderRef.current) return;

                                        const rect = sliderRef.current.getBoundingClientRect();
                                        const deltaX = moveEvent.clientX - startMouseX;
                                        const deltaPercent = (deltaX / rect.width) * 100;
                                        const newValue = Math.max(yearRange[0], Math.min(100, startValue + deltaPercent));

                                        handleYearRangeChange(1, newValue);
                                    };

                                    const handleMouseUp = () => {
                                        document.removeEventListener('mousemove', handleMouseMove);
                                        document.removeEventListener('mouseup', handleMouseUp);
                                    };

                                    document.addEventListener('mousemove', handleMouseMove);
                                    document.addEventListener('mouseup', handleMouseUp);
                                }}
                            >
                                <div style={{
                                    width: '6px',
                                    height: '6px',
                                    backgroundcolor: "var(--text-primary)",
                                    borderRadius: '50%'
                                }} />
                            </div>
                        </div> */}

                            {/* <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '12px',
                            color: '#9CA3AF',
                            position: 'relative'
                        }}>
                            <span>{isQuarterlyData ? periods[0] : availableYearRange.min}</span>
                            {!isQuarterlyData && periodMarkers.length > 4 && periodMarkers.length <= 10 && (
                                periodMarkers.slice(1, -1).filter((_, index) => index % 2 === 0).map(marker => (
                                    <span
                                        key={marker.year}
                                        style={{
                                            position: 'absolute',
                                            left: `${marker.position}%`,
                                            transform: 'translateX(-50%)',
                                            fontSize: '10px'
                                        }}
                                    >
                                        {marker.year}
                                    </span>
                                ))
                            )}
                            <span>{isQuarterlyData ? periods[periods.length - 1] : availableYearRange.max}</span>
                        </div> */}
                        </>
                        )}

                    </div>
                )}
            </div>
        </div>
    );
};

export default MChart;
